
































































































import Vue from "vue";
import Component from "vue-class-component";
import {Model, Prop, Watch} from "vue-property-decorator";
import ValidatedVCard from "@/components/ValidatedVCard.vue";
import isEqual from "lodash/isEqual";

const modelEvent = "change";

@Component({
    components: {ValidatedVCard}
})
export default class EditableList extends Vue {

    deletedCacheMap: Map<any, boolean> = new Map();
    deletedCacheUpdater: boolean = false;

    @Model(modelEvent, {required: true}) readonly items!: any[];
    @Prop() readonly add?: () => any;

    @Prop() readonly errorMessages?: string[];

    @Prop() readonly title?: string;
    @Prop({type: [String, Function]}) readonly itemKey?: string | ((item: any, index: number) => any);

    @Prop() readonly addText?: string;
    @Prop() readonly emptyText?: string;

    @Prop() readonly removeText?: string;
    @Prop() readonly restoreText?: string;
    @Prop() readonly moveUpText?: string;
    @Prop() readonly moveDownText?: string;

    @Prop() readonly restoreMessage?: string;

    @Prop({default: () => false}) readonly canOrder!: boolean;
    @Prop({default: () => false}) readonly markDeleted!: boolean;
    @Prop() readonly markDeletedFilter?: (item: any, index: number, key: any) => boolean;

    @Prop({default: () => "fit-content"}) readonly contentMinWidth!: string;
    @Prop({default: () => "fit-content"}) readonly appendMinWidth!: string;
    @Prop({default: () => "500px"}) readonly maxContentHeight!: string;

    @Prop({default: () => "warning lighten-5"}) readonly restoreClass!: string;

    @Prop({default: () => []}) readonly deletedItems!: any[];

    get deletedCache() {
        return this.deletedCacheUpdater ? this.deletedCacheMap : this.deletedCacheMap;
    }

    get keys(): any[] {
        return this.items.map((item, index) => this.getKey(item, index));
    }

    @Watch("keys", {immediate: true})
    onKeys(keys: any[]) {
        const newDeletedCache: Map<any, boolean> = new Map();

        for (let key of keys) {
            if (this.deletedCache.has(key)) {
                newDeletedCache.set(key, this.deletedCache.get(key) as any);
            } else {
                newDeletedCache.set(key, false);
            }
        }

        this.deletedCacheMap = newDeletedCache;
        this.markDeletedCacheForUpdate();

        this.updateDeletedItems();
    }

    @Watch("deletedCache")
    onDeletedCache() {
        this.updateDeletedItems();
    }

    @Watch("deletedItems", {immediate: true})
    onDeletedItems(newDeletedItems: any[]) {
        const mappedItems = newDeletedItems.map(newDeletedItem => ({
            item: newDeletedItem,
            index: this.items.findIndex(item => item === newDeletedItem)
        })).filter(mappedItem => mappedItem.index >= 0);

        let needsUpdate = false;
        for (let mappedItem of mappedItems) {
            const key = this.getKey(mappedItem.item, mappedItem.index);

            if (this.deletedCache.get(key) !== true) {
                this.deletedCache.set(key, true);
                needsUpdate = true;
            }
        }

        if (needsUpdate) this.markDeletedCacheForUpdate();
    }

    updateDeletedItems() {
        const newDeletedItems = [...this.deletedCache]
            .map(([key, deleted], index) => ({deleted, index}))
            .filter(({deleted}) => deleted)
            .map(({index}) => this.items[index]);

        if (!isEqual(this.deletedItems, newDeletedItems)) this.$emit("update:deletedItems", newDeletedItems);
    }

    markDeletedCacheForUpdate() {
        this.deletedCacheUpdater = !this.deletedCacheUpdater;
    }

    getKey(item: any, index: number): any {
        return this.itemKey ? (typeof this.itemKey === "string" ? item[this.itemKey] : this.itemKey(item, index)) : index;
    }

    addItem() {
        if (this.add) {
            const items = this.items.map(item => item);
            items.push(this.add());
            this.$emit(modelEvent, items);
        }
    }

    moveItem(index: number, targetIndex: number) {
        const items = this.items.map(item => item);
        const [item] = items.splice(index, 1);
        items.splice(targetIndex, 0, item);
        this.$emit(modelEvent, items);
    }

    removeItem(index: number, key: any) {
        if (this.markDeleted && (!this.markDeletedFilter || this.markDeletedFilter(this.items[index], index, key))) {
            this.deletedCache.set(key, true);
            this.markDeletedCacheForUpdate();
        } else {
            const items = this.items.map(item => item);
            items.splice(index, 1);
            this.$emit(modelEvent, items);
        }
    }

    restoreItem(key: any) {
        this.deletedCache.set(key, false);
        this.markDeletedCacheForUpdate();
    }

}
